import axiosNest from '../../api/axiosNest';

/**
 * Przełączniki funkcji „do decyzji” i wybór wersji — odpowiada
 * `/feature-flags` i `PATCH /users/me/version` w API (decyzja użytkownika
 * 12.10.2026, `apps/api/src/feature-flags/README-feature-flags.md`).
 *
 * Odczyt dla interfejsu: `GET /feature-flags/effective` — co działa dla
 * zalogowanego po wyborze wersji. Jedno zapytanie na sesję; wynik trzymamy
 * w pamięci i odświeżamy po zmianie przełącznika albo wersji, żeby menu
 * i sekcje od razu się przeliczyły (wzór: `companyModulesApi.ts`).
 *
 * Front tylko ukrywa — trasy funkcji za przełącznikiem pilnuje API (404).
 */

/** Wersja konta — dane z API, nie tłumaczymy. */
export type FeatureVersion = 'current' | 'new';

export type EffectiveFlags = {
  flags: Record<string, boolean>;
  /** `null` = środowisko bez wyboru wersji (produkcja). */
  version: FeatureVersion | null;
  versionSwitchAvailable: boolean;
  /**
   * Profil wydania (25.10.2026, S-25.10-02): działają tylko wydawane paczki,
   * wyboru wersji nie ma. Brak pola (starsze API, środowisko bez profilu) = nie.
   */
  releaseProfile?: boolean;
  /** Ekrany ukryte z menu przez wyłączoną paczkę (zasoby); brak = żadne. */
  hiddenMenu?: string[];
};

/** Nazwy wersji — te same co w API (`VERSION_LABELS`). */
export const VERSION_LABELS: Record<FeatureVersion, string> = {
  current: 'obecna (jak na produkcji)',
  new: 'nowa (do akceptacji)',
};

const isVersion = (v: unknown): v is FeatureVersion => v === 'current' || v === 'new';

/** Odpowiedź API w bezpiecznym kształcie — nieznane pole to brak, nie zgadywanie. */
export function normalizeEffective(data: unknown): EffectiveFlags {
  const d = (data ?? {}) as Record<string, unknown>;
  const raw = d.flags && typeof d.flags === 'object' ? (d.flags as Record<string, unknown>) : {};
  const flags: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw)) flags[k] = v === true;
  const versionSwitchAvailable = d.versionSwitchAvailable === true;
  return {
    flags,
    version: versionSwitchAvailable && isVersion(d.version) ? d.version : null,
    versionSwitchAvailable,
    // Pola profilu wydania tylko wtedy, gdy przyszły — poza profilem kształt jak dotąd.
    ...(d.releaseProfile === true ? { releaseProfile: true } : {}),
    ...(Array.isArray(d.hiddenMenu)
      ? { hiddenMenu: d.hiddenMenu.filter((r): r is string => typeof r === 'string') }
      : {}),
  };
}

let cache: Promise<EffectiveFlags | null> | null = null;
const listeners = new Set<(state: EffectiveFlags | null) => void>();

/**
 * Stan odczytu dla interfejsu (integracja przełączników 12.10.2026):
 * `loading` — pierwszy odczyt w sesji jeszcze trwa; `ready` — przełączniki
 * są; `error` — odczyt się nie udał (funkcje za przełącznikiem ukryte,
 * czyli wersja „obecna”). Trzymany synchronicznie, żeby każdy kolejny
 * ekran w sesji od razu rysował właściwą wersję — bez mignięcia „obecnej”
 * przed „nową”, które dawał hak startujący od „wyłączony”.
 */
export type FlagsStatus = 'loading' | 'ready' | 'error';
export type FlagsSnapshot = { status: FlagsStatus; state: EffectiveFlags | null };

const LOADING: FlagsSnapshot = { status: 'loading', state: null };
let snapshot: FlagsSnapshot = LOADING;
const snapshotListeners = new Set<() => void>();

/** Bieżący stan odczytu — dla `useSyncExternalStore`. */
export function getFlagsSnapshot(): FlagsSnapshot {
  return snapshot;
}

/** Powiadomienie o każdej zmianie stanu odczytu. */
export function subscribeFlagsSnapshot(listener: () => void): () => void {
  snapshotListeners.add(listener);
  return () => {
    snapshotListeners.delete(listener);
  };
}

function setSnapshot(next: FlagsSnapshot) {
  snapshot = next;
  snapshotListeners.forEach((l) => l());
}

/**
 * Limit czasu odczytu: przełączniki wstrzymują ekrany zależne od wersji,
 * więc zawieszone zapytanie nie może ich trzymać w nieskończoność — po nim
 * wersja „obecna” (jak przy błędzie).
 */
export const FLAGS_LOAD_TIMEOUT_MS = 10_000;

/**
 * Przełączniki dla zalogowanego — jeden odczyt na sesję (wspólny dla całej
 * aplikacji). Błąd odczytu = `null` (nie wiemy) i bez zapamiętania —
 * kolejne wejście spyta jeszcze raz. Funkcje za przełącznikiem są wtedy
 * ukryte: zachowanie jak przed zmianą.
 */
export function loadEffectiveFlags(): Promise<EffectiveFlags | null> {
  if (!cache) {
    cache = Promise.resolve()
      .then(() => axiosNest.get('/feature-flags/effective', { timeout: FLAGS_LOAD_TIMEOUT_MS }))
      .then((res) => {
        const state = normalizeEffective(res?.data);
        setSnapshot({ status: 'ready', state });
        return state;
      })
      .catch(() => {
        cache = null;
        // Wcześniej wczytany stan zostaje (ponowny odczyt po zmianie, który
        // się nie udał); bez niego — wersja „obecna”.
        if (snapshot.status === 'loading') setSnapshot({ status: 'error', state: null });
        return null;
      });
  }
  return cache;
}

/** Powiadomienie o zmianie — hak `useFeatureFlags` przelicza widok. */
export function subscribeEffectiveFlags(listener: (state: EffectiveFlags | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(state: EffectiveFlags | null) {
  if (state) setSnapshot({ status: 'ready', state });
  listeners.forEach((l) => l(state));
}

/** Odczyt od nowa i powiadomienie wszystkich, którzy pokazują funkcje. */
export async function reloadEffectiveFlags(): Promise<void> {
  cache = null;
  publish(await loadEffectiveFlags());
}

/**
 * Zapomnij przełączniki poprzedniego konta (logowanie, wylogowanie) —
 * następny ekran poczeka na odczyt dla nowego konta.
 */
export function forgetEffectiveFlags(): void {
  cache = null;
  setSnapshot(LOADING);
}

/** Tylko dla testów: zapomnij zapamiętany odczyt. */
export function resetEffectiveFlagsCache(): void {
  cache = null;
  snapshot = LOADING;
  listeners.clear();
  snapshotListeners.clear();
}

/** Wybór wersji własnego konta; odpowiedź od razu trafia do wszystkich haków. */
export async function saveOwnVersion(version: FeatureVersion): Promise<EffectiveFlags> {
  const { data } = await axiosNest.patch('/users/me/version', { version });
  const state = normalizeEffective(data);
  cache = Promise.resolve(state);
  publish(state);
  return state;
}

// ── Ekran administracyjny ─────────────────────────────────────────────

export type FlagChange = {
  id: number;
  key: string;
  /** `null` = wcześniej działał stan domyślny środowiska. */
  oldValue: boolean | null;
  newValue: boolean;
  reason: string;
  changedAt: string;
  changedById: number | null;
  changedByName: string | null;
};

/** Decyzja o funkcji (13.10.2026) — dane z API, nie tłumaczymy. */
export type DecisionStatus = 'pending' | 'accepted' | 'rework' | 'rejected';

export type FlagDecision = {
  /** `pending` także wtedy, gdy decyzji nikt jeszcze nie zapisał. */
  status: DecisionStatus;
  comment: string | null;
  decidedAt: string | null;
  decidedById: number | null;
  decidedByName: string | null;
};

export type DecisionChange = {
  id: number;
  key: string;
  /** `null` = wcześniej nie było decyzji („do decyzji”). */
  oldStatus: string | null;
  newStatus: string;
  comment: string | null;
  changedAt: string;
  changedById: number | null;
  changedByName: string | null;
};

export type FeatureFlag = {
  id: string;
  key: string;
  label: string;
  description: string;
  since: string;
  /** Stan na tym środowisku (bez wyboru wersji konta). */
  enabled: boolean;
  /** `false` = działa stan domyślny środowiska. */
  configured: boolean;
  defaultEnabled: boolean;
  updatedAt: string | null;
  lastChange: FlagChange | null;
  /** Pozycje prezentacji różnicowej (`S-12.10-01`); brak w starszym API = pusto. */
  presentationItems?: string[];
  /** Decyzja; brak w starszym API = „do decyzji”. */
  decision?: FlagDecision;
  /** Paczka wydania (25.10.2026); brak = poza paczkami. */
  bundle?: string | null;
  /** Profil wydania trzyma przełącznik wyłączony (spoza paczek); brak = nie. */
  lockedByProfile?: boolean;
};

export async function fetchFlags(): Promise<FeatureFlag[]> {
  const { data } = await axiosNest.get<FeatureFlag[]>('/feature-flags');
  return Array.isArray(data) ? data : [];
}

export async function fetchFlagHistory(key?: string | null): Promise<FlagChange[]> {
  const { data } = await axiosNest.get<FlagChange[]>('/feature-flags/history', {
    params: key ? { key } : {},
  });
  return Array.isArray(data) ? data : [];
}

/** Czy zalogowany może zmieniać przełączniki — ta sama reguła co strażnik zapisu. */
export async function fetchFlagAccess(): Promise<{ canEdit: boolean; requiredPermission: string }> {
  const { data } = await axiosNest.get('/feature-flags/access');
  return { canEdit: data?.canEdit === true, requiredPermission: String(data?.requiredPermission ?? '') };
}

export async function saveFlag(key: string, enabled: boolean, reason: string): Promise<FeatureFlag> {
  const { data } = await axiosNest.patch<FeatureFlag>(`/feature-flags/${encodeURIComponent(key)}`, {
    enabled,
    reason,
  });
  return data;
}

/** Decyzja o funkcji — stanu przełącznika nie zmienia (osobna czynność). */
export async function saveDecision(key: string, status: DecisionStatus, comment: string): Promise<FeatureFlag> {
  const { data } = await axiosNest.put<FeatureFlag>(`/feature-flags/${encodeURIComponent(key)}/decision`, {
    status,
    comment,
  });
  return data;
}

export async function fetchDecisionHistory(key: string): Promise<DecisionChange[]> {
  const { data } = await axiosNest.get<DecisionChange[]>(
    `/feature-flags/${encodeURIComponent(key)}/decision-history`,
  );
  return Array.isArray(data) ? data : [];
}

/** Próba przełącznika po stronie API: 200 = włączony, 404 = wyłączony. */
export async function probeTrialRoute(): Promise<boolean> {
  try {
    await axiosNest.get('/feature-flags/trial');
    return true;
  } catch {
    return false;
  }
}

/** Stan słownie, z nazwanym stanem domyślnym. */
export const flagStateLabel = (enabled: boolean | null, defaultEnabled: boolean) =>
  enabled === null
    ? `${defaultEnabled ? 'włączony' : 'wyłączony'} (domyślnie)`
    : enabled
      ? 'włączony'
      : 'wyłączony';

// ── Paczki wydania i profil wydania (25.10.2026, S-25.10-02) ─────────────

export type ReleaseProfile = {
  active: boolean;
  bundles: string[];
  unknownBundles: string[];
};

export type BundleChange = {
  id: number;
  key: string;
  /** `enabled` / `hideMenuWhenOff` — dane z API. */
  field: string;
  oldValue: boolean | null;
  newValue: boolean;
  reason: string;
  changedAt: string;
  changedById: number | null;
  changedByName: string | null;
};

export type FeatureBundle = {
  id: string;
  key: string;
  label: string;
  description: string;
  since: string;
  enabled: boolean;
  hideMenuWhenOff: boolean;
  configured: boolean;
  /** Czy paczka jest wydawana w profilu tego środowiska. */
  inProfile: boolean;
  /**
   * Czy włącznik paczki decyduje o wersji „obecnej (jak na produkcji)” —
   * środowisko z wyborem wersji, paczka wydana na produkcji (29.10.2026).
   * Starsze API pola nie ma — wtedy `undefined`, czytane jak „nie”.
   */
  inCurrentVersion?: boolean;
  flags: Array<{ key: string; label: string; presentationItems: string[] }>;
  releasedMenuResources: string[];
  updatedAt: string | null;
  lastChange: BundleChange | null;
};

export type BundlesOverview = { profile: ReleaseProfile; bundles: FeatureBundle[] };

export async function fetchBundles(): Promise<BundlesOverview> {
  const { data } = await axiosNest.get<BundlesOverview>('/feature-flags/bundles');
  return {
    profile: {
      active: data?.profile?.active === true,
      bundles: Array.isArray(data?.profile?.bundles) ? data.profile.bundles : [],
      unknownBundles: Array.isArray(data?.profile?.unknownBundles) ? data.profile.unknownBundles : [],
    },
    bundles: Array.isArray(data?.bundles) ? data.bundles : [],
  };
}

export async function fetchBundleHistory(key: string): Promise<BundleChange[]> {
  const { data } = await axiosNest.get<BundleChange[]>(`/feature-flags/bundles/${encodeURIComponent(key)}/history`);
  return Array.isArray(data) ? data : [];
}

/** Zmiana paczki — jedno pole naraz, z powodem; po zapisie menu i sekcje przeliczają się od razu. */
export async function saveBundle(
  key: string,
  change: { enabled?: boolean; hideMenuWhenOff?: boolean },
  reason: string,
): Promise<BundlesOverview> {
  const { data } = await axiosNest.patch<BundlesOverview>(`/feature-flags/bundles/${encodeURIComponent(key)}`, {
    ...change,
    reason,
  });
  await reloadEffectiveFlags();
  return data;
}
